// ================================================================
// App.tsx — Root: SetupWizard gate → AuthProvider → Router
// ================================================================

import { useState, useEffect, createContext, useContext, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AccountInfo } from '@azure/msal-browser';
import { login, logout, getActiveAccount, getUserRoles } from './auth';
import { fetchMyTenant, TenantInfo, BASE_URL } from './api';
import Layout from './components/Layout';
import SetupWizard from './pages/SetupWizard';
import { getConfig } from './config';
import { getApiKeySession, loginWithClientCredentials, clearApiKeySession, ApiKeySession } from './apikey-session';
import { isDemoMode, enableDemoMode, disableDemoMode, DEMO_SESSION, DEMO_TENANT } from './demo';
import { C } from './styles';

// Lazy-loaded page components
const UsageDashboard  = lazy(() => import('./pages/UsageDashboard'));
const ActivityLog     = lazy(() => import('./pages/ActivityLog'));
const AuditViewer     = lazy(() => import('./pages/AuditViewer'));
const TenantSettings  = lazy(() => import('./pages/TenantSettings'));
const AgentScanner    = lazy(() => import('./pages/AgentScanner'));

// ── Auth context ──────────────────────────────────────────────
interface AuthState {
  account: AccountInfo | null;
  apiKeySession: ApiKeySession | null;
  roles: string[];
  tenant: TenantInfo | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState>({
  account: null, apiKeySession: null, roles: [], tenant: null, loading: true,
});

export function useAuth() { return useContext(AuthContext); }

// ── AuthProvider ──────────────────────────────────────────────
function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    account: null, apiKeySession: null, roles: [], tenant: null, loading: true,
  });

  useEffect(() => {
    (async () => {
      try {
        // Demo 模式優先（無需呼叫 Workers）
        if (isDemoMode()) {
          setState({
            account: null,
            apiKeySession: DEMO_SESSION,
            roles: DEMO_SESSION.roles,
            tenant: DEMO_TENANT,
            loading: false,
          });
          return;
        }
        // API Key session 優先
        const akSession = getApiKeySession();
        if (akSession) {
          const tenantRes = await fetchMyTenant().catch(() => null);
          setState({
            account: null,
            apiKeySession: akSession,
            roles: akSession.roles,
            tenant: tenantRes?.data ?? null,
            loading: false,
          });
          return;
        }
        // MSAL flow
        const account = await getActiveAccount();
        if (!account) {
          setState({ account: null, apiKeySession: null, roles: [], tenant: null, loading: false });
          return;
        }
        const [roles, tenantRes] = await Promise.all([
          getUserRoles(),
          fetchMyTenant().catch(() => null),
        ]);
        setState({ account, apiKeySession: null, roles, tenant: tenantRes?.data ?? null, loading: false });
      } catch {
        setState({ account: null, apiKeySession: null, roles: [], tenant: null, loading: false });
      }
    })();
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

// ── Login screen ──────────────────────────────────────────────
function LoginScreen() {
  const [tab, setTab]         = useState<'sso' | 'sp'>('sso');
  const [demoHover, setDemoHover] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  // Client Credentials 欄位
  const [tenantId, setTenantId]       = useState('');
  const [clientId, setClientId]       = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const handleSsoLogin = async () => {
    setBusy(true); setErr(null);
    const account = await login();
    if (account) { window.location.reload(); }
    else { setErr('登入失敗，請再試一次。'); setBusy(false); }
  };

  const handleSpLogin = async () => {
    if (!tenantId.trim()) { setErr('請輸入 Tenant ID'); return; }
    if (!clientId.trim())  { setErr('請輸入 Client ID'); return; }
    if (!clientSecret.trim()) { setErr('請輸入用戶端密碼（Client Secret）'); return; }
    setBusy(true); setErr(null);
    try {
      await loginWithClientCredentials(tenantId.trim(), clientId.trim(), clientSecret.trim(), BASE_URL);
      window.location.reload();
    } catch (e) {
      // Entra 錯誤說明通常很長，只取第一句
      const msg = e instanceof Error ? e.message.split('\r\n')[0].substring(0, 200) : '驗證失敗';
      setErr(msg);
      setBusy(false);
    }
  };

  const tabStyle = (active: boolean) => ({
    flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer',
    background: active ? C.bg1 : C.bg2,
    color: active ? C.t1 : C.t4,
    fontWeight: active ? 700 : 400, fontSize: 13,
    borderBottom: active ? `2px solid ${C.blue}` : `2px solid transparent`,
    transition: 'all .15s',
  } as React.CSSProperties);

  const inputStyle: React.CSSProperties = {
    width: '100%', background: C.bg2, border: `1px solid ${C.border}`,
    borderRadius: 8, color: C.t1, padding: '10px 14px', fontSize: 13,
    fontFamily: 'monospace', outline: 'none', marginBottom: 12,
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', color: C.t3, fontSize: 11, fontWeight: 600,
    marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase',
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: C.bg0,
    }}>
      <div style={{
        background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 16,
        width: '100%', maxWidth: 440, boxShadow: '0 8px 48px rgba(0,0,0,.4)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '40px 48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🛡️</div>
          <h1 style={{ color: C.t1, fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>
            AI Governance Platform
          </h1>
          <p style={{ color: C.t3, fontSize: 13, margin: 0, lineHeight: 1.6 }}>
            Microsoft 365 Copilot 使用監控 &amp; AI 活動稽核平台
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
          <button style={tabStyle(tab === 'sso')} onClick={() => { setTab('sso'); setErr(null); }}>
            🔐 Microsoft SSO
          </button>
          <button style={tabStyle(tab === 'sp')}  onClick={() => { setTab('sp');  setErr(null); }}>
            🤖 服務帳號登入
          </button>
        </div>

        {/* Tab content */}
        <div style={{ padding: '28px 48px 40px' }}>
          {tab === 'sso' && (
            <>
              <p style={{ color: C.t3, fontSize: 13, margin: '0 0 24px', lineHeight: 1.6 }}>
                使用您的企業 Microsoft 365 帳號登入
              </p>
              <button
                onClick={handleSsoLogin} disabled={busy}
                style={{
                  background: busy ? C.bg3 : C.blue, border: 'none', color: '#fff',
                  padding: '13px 0', borderRadius: 10, fontSize: 15, fontWeight: 600,
                  cursor: busy ? 'not-allowed' : 'pointer', width: '100%',
                  transition: 'background .2s',
                }}
              >
                {busy ? '登入中…' : '使用 Microsoft 帳號登入'}
              </button>
            </>
          )}

          {tab === 'sp' && (
            <>
              <p style={{ color: C.t3, fontSize: 13, margin: '0 0 16px', lineHeight: 1.6 }}>
                使用 Entra App Registration 的用戶端憑證（Client Credentials）登入。
                適用於自動化流程或無法使用 SSO 的情境。
              </p>

              <label style={labelStyle}>Directory (Tenant) ID</label>
              <input
                type="text"
                value={tenantId}
                onChange={e => setTenantId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                style={inputStyle}
                onFocus={e => (e.target.style.borderColor = C.blue)}
                onBlur={e => (e.target.style.borderColor = C.border as string)}
              />

              <label style={labelStyle}>Application (Client) ID</label>
              <input
                type="text"
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                style={inputStyle}
                onFocus={e => (e.target.style.borderColor = C.blue)}
                onBlur={e => (e.target.style.borderColor = C.border as string)}
              />

              <label style={labelStyle}>用戶端密碼（Client Secret 值）</label>
              <input
                type="password"
                value={clientSecret}
                onChange={e => setClientSecret(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSpLogin()}
                placeholder="輸入 Client Secret 的值（非 Secret ID）"
                style={inputStyle}
                onFocus={e => (e.target.style.borderColor = C.blue)}
                onBlur={e => (e.target.style.borderColor = C.border as string)}
              />

              <button
                onClick={handleSpLogin} disabled={busy}
                style={{
                  background: busy ? C.bg3 : C.green, border: 'none', color: '#fff',
                  padding: '13px 0', borderRadius: 10, fontSize: 15, fontWeight: 600,
                  cursor: busy ? 'not-allowed' : 'pointer', width: '100%',
                  transition: 'background .2s', marginTop: 4,
                }}
              >
                {busy ? '驗證中…' : '以服務帳號登入'}
              </button>

              <div style={{
                background: C.bg2, borderRadius: 8, padding: '10px 14px',
                marginTop: 14, fontSize: 11, color: C.t4, lineHeight: 1.6,
              }}>
                ℹ️ 在 <strong style={{ color: C.t3 }}>Entra → App registrations → 憑證及密碼 → 用戶端密碼</strong> 取得「值」欄位。
                服務帳號 session 有效期為 <strong style={{ color: C.t3 }}>1 小時</strong>，到期後需重新登入。
              </div>
            </>
          )}

          {err && (
            <div style={{
              color: C.red, fontSize: 12, marginTop: 16,
              background: C.red + '11', border: `1px solid ${C.red}33`,
              borderRadius: 8, padding: '10px 14px', wordBreak: 'break-word',
            }}>
              ⚠️ {err}
            </div>
          )}

          <p style={{ color: C.t4, fontSize: 11, marginTop: 20, lineHeight: 1.6, textAlign: 'center' }}>
            登入後資料依 Entra tenant 隔離。每次檢視均寫入稽核日誌。
          </p>
        </div>

        {/* ── Demo 模式入口 ── */}
        <div style={{
          borderTop: `1px solid ${C.border}`,
          padding: '20px 48px 28px',
          background: C.bg2,
        }}>
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            <span style={{ color: C.t4, fontSize: 11 }}>— 或 —</span>
          </div>
          <button
            onClick={() => { enableDemoMode(); window.location.reload(); }}
            onMouseEnter={() => setDemoHover(true)}
            onMouseLeave={() => setDemoHover(false)}
            style={{
              width: '100%', padding: '11px 0', borderRadius: 10,
              border: `1px dashed ${demoHover ? C.purple : C.border}`,
              background: demoHover ? C.purple + '11' : 'transparent',
              color: demoHover ? C.purple : C.t3,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              transition: 'all .15s',
            }}
          >
            🎮 試用 Demo（不需要登入）
          </button>
          <p style={{ color: C.t4, fontSize: 11, textAlign: 'center', marginTop: 8, lineHeight: 1.5 }}>
            使用模擬資料體驗所有功能，不連接任何真實帳號
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Role guard ────────────────────────────────────────────────
export function RequireRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { roles: userRoles } = useAuth();
  if (!roles.some(r => userRoles.includes(r))) {
    return (
      <div style={{ color: C.red, padding: 60, textAlign: 'center', fontSize: 15 }}>
        ⛔ 權限不足 — 需要角色：{roles.join(' / ')}
      </div>
    );
  }
  return <>{children}</>;
}

// ── Page loading fallback ─────────────────────────────────────
function PageLoader() {
  return (
    <div style={{ padding: 60, textAlign: 'center', color: C.t4, fontSize: 14 }}>
      <div style={{ fontSize: 28, marginBottom: 12 }}>⌛</div>
      載入中…
    </div>
  );
}

// ── App root ──────────────────────────────────────────────────
// 啟動順序：
//   1. getConfig() === null   → 顯示 SetupWizard（首次使用）
//   2. config 存在，未登入    → 顯示 LoginScreen
//   3. 已登入                 → 顯示主應用
export default function App() {
  const hasConfig = getConfig() !== null;

  if (!hasConfig) {
    return <SetupWizard />;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { account, apiKeySession, loading } = useAuth();
  const isLoggedIn = !!account || !!apiKeySession;

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: C.bg0, color: C.t3, fontSize: 14,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>🛡️</div>
          驗證中，請稍候…
        </div>
      </div>
    );
  }

  if (!isLoggedIn) return <LoginScreen />;

  return (
    <BrowserRouter>
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/"         element={<UsageDashboard />} />
            <Route path="/activity" element={<ActivityLog />} />
            <Route path="/audit"    element={<AuditViewer />} />
            <Route path="/agents"   element={<AgentScanner />} />
            <Route path="/settings" element={<TenantSettings />} />
            {/* Consent callback — redirect to settings so user sees status update */}
            <Route path="/auth/callback" element={<Navigate to="/settings" replace />} />
            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
}
